import { useRef } from "react";
import type { TFunction } from "i18next";
import { m, useScroll, useTransform, type MotionValue } from "motion/react";
import { Check, ArrowRight, FileText, Building2, Brush, HardHat, CalendarClock } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { Container, Eyebrow, PostCard } from "../components/ui";
import {
  MotionProvider, Reveal, Workflow, type WorkflowStep,
  PromoBanner, Button as DSButton, PageHeading, SectionHeading,
} from "../ds/core";
import { postsFor, POST_DATE, coverFor } from "../content/posts";
import { featureLiveForMarket } from "../content/flags";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const U = (id: string) => `https://images.unsplash.com/${id}?w=1600&auto=format&fit=crop&q=80`;
const PHOTO = U("photo-1486006920555-c77dcf18193c");

const GRID_W = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
} as const;
const GOLD_GRAD = "linear-gradient(to bottom, var(--ds-warn), var(--ds-gold))";
const SCRIM = "linear-gradient(180deg, rgba(8,11,22,0.2) 0%, rgba(8,11,22,0.5) 100%)";

const changeStepsFor = (t: TFunction): WorkflowStep[] => [
  { n: "01", title: t("legal:changes.1.title"), body: t("legal:changes.1.body") },
  { n: "02", title: t("legal:changes.2.title"), body: t("legal:changes.2.body") },
  { n: "03", title: t("legal:changes.3.title"), body: t("legal:changes.3.body") },
];

// What already applies today — the layered landscape the 2027 law sits on top of.
const sectorsFor = (t: TFunction) => [
  { icon: Building2, name: t("legal:sectors.checkin.name"), since: t("legal:sectors.checkin.since"), body: t("legal:sectors.checkin.body"), slug: "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie" },
  { icon: Brush, name: t("legal:sectors.ciao.name"), since: t("legal:sectors.ciao.since"), body: t("legal:sectors.ciao.body"), slug: "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie" },
  { icon: HardHat, name: t("legal:sectors.fullReg.name"), since: t("legal:sectors.fullReg.since"), body: t("legal:sectors.fullReg.body"), slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten" },
  { icon: FileText, name: t("legal:sectors.ec32a.name"), since: t("legal:sectors.ec32a.since"), body: t("legal:sectors.ec32a.body"), slug: "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a" },
];

const timelineFor = (t: TFunction) => [
  { date: t("legal:timeline.2014.date"), title: t("legal:timeline.2014.title"), body: t("legal:timeline.2014.body") },
  { date: t("legal:timeline.2024.date"), title: t("legal:timeline.2024.title"), body: t("legal:timeline.2024.body") },
  { date: t("legal:timeline.2025.date"), title: t("legal:timeline.2025.title"), body: t("legal:timeline.2025.body") },
  { date: t("legal:timeline.2026.date"), title: t("legal:timeline.2026.title"), body: t("legal:timeline.2026.body") },
  { date: t("legal:timeline.2027.date"), title: t("legal:timeline.2027.title"), body: t("legal:timeline.2027.body") },
];

const checklistFor = (t: TFunction) => [
  t("legal:checklist.1"),
  t("legal:checklist.2"),
  t("legal:checklist.3"),
  t("legal:checklist.4"),
  t("legal:checklist.5"),
];

/* A timeline dot that lights up (fades + scales to full) exactly as the filling
   line passes its position — `start` is its fraction along the track. */
function TimelineDot({ progress, start }: { progress: MotionValue<number>; start: number }) {
  const scale = useTransform(progress, [start - 0.04, start + 0.01], [0.55, 1]);
  const opacity = useTransform(progress, [start - 0.04, start + 0.01], [0.25, 1]);
  return (
    <m.span
      className="absolute left-1/2 top-1/2 hidden h-4 w-4 rounded-full bg-ds-ink ring-4 ring-white/70 lg:block"
      style={{ x: "-50%", y: "-50%", scale, opacity }}
      aria-hidden
    />
  );
}

/* ── Animated centre timeline: a gold panel with a vertical track that fills as
      it enters view, dots popping in, and cards alternating left / right (a
      single stacked column on mobile). */
function GoldTimeline() {
  const { t } = useLocale();
  const TIMELINE = timelineFor(t);
  const trackRef = useRef<HTMLDivElement>(null);
  // The centre line fills in lock-step with the scroll through the track.
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start 75%", "end 65%"] });

  return (
    <MotionProvider>
      <section className="bg-bg-subtle font-ds">
        <div className="px-4 py-3">
          <div className="relative overflow-hidden rounded-[32px] py-16 md:py-24" style={{ background: GOLD_GRAD }}>
            <div className="pointer-events-none absolute inset-0" style={GRID_W} />
            <Container className="relative">
              <SectionHeading align="center" tone="dark" size="lg" eyebrow={t("legal:timeline.eyebrow")} title={t("legal:timeline.title")} />

              <div ref={trackRef} className="relative mx-auto mt-14 max-w-3xl">
                {/* Centre track + scroll-linked fill (desktop only) */}
                <span className="absolute left-1/2 top-2 bottom-2 hidden w-[3px] -translate-x-1/2 rounded-full bg-white/35 lg:block" aria-hidden />
                <m.span
                  className="absolute left-1/2 top-2 bottom-2 hidden w-[3px] rounded-full bg-ds-ink lg:block"
                  style={{ scaleY: scrollYProgress, x: "-50%", transformOrigin: "top" }} aria-hidden
                />

                <div className="flex flex-col gap-6 lg:gap-10">
                  {TIMELINE.map((entry, i) => {
                    const left = i % 2 === 0;
                    return (
                      <div key={entry.date} className="relative lg:grid lg:grid-cols-2 lg:items-center lg:gap-x-12">
                        {/* Dot — lights up when the filling line reaches it */}
                        <TimelineDot progress={scrollYProgress} start={(i + 0.5) / TIMELINE.length} />
                        <Reveal className={`flex ${left ? "lg:col-start-1 lg:justify-end" : "lg:col-start-2"}`}>
                          <div className="w-full rounded-2xl border-8 border-white bg-bg-subtle p-5 md:p-6">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-ink px-2.5 py-1 text-[12px] font-semibold text-white">
                              <CalendarClock size={13} strokeWidth={2.2} /> {entry.date}
                            </span>
                            <h3 className="mt-3 font-display text-ds-18 font-semibold text-ds-ink">{entry.title}</h3>
                            <p className="mt-1.5 text-ds-14 leading-relaxed text-ds-ink-soft">{entry.body}</p>
                          </div>
                        </Reveal>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Container>
          </div>
        </div>
      </section>
    </MotionProvider>
  );
}

export default function Law2027() {
  const { t, locale, market } = useLocale();
  // Belgium-only AND gated by the `law_2027` kill-switch. The page is dropped from
  // the prerender matrix when the flag is off, but this runtime guard is defence in
  // depth: a stale cached HTML (or the CSR shell) still shows "not available".
  if (market !== "belgium" || !featureLiveForMarket(market, "law_2027")) {
    return (
      <section className="bg-bg-subtle font-ds">
        <Container className="flex flex-col items-center gap-5 py-32 text-center">
          <p className="font-display text-ds-18 font-semibold text-ds-ink">{t("legal:notAvailable.title")}</p>
          <DSButton to="/" variant="solid">{t("legal:notAvailable.cta")}</DSButton>
        </Container>
      </section>
    );
  }

  // Belgium-only computations live below the market guard so they never run for
  // the international fallback (and can't leak intl articles into a Belgian-law
  // section if the guard ever moves).
  const RELATED = postsFor(locale).filter((p) => p.tag === "Compliance").slice(0, 4);
  const SECTORS = sectorsFor(t);
  const CHECKLIST = checklistFor(t);
  const changeSteps = changeStepsFor(t);

  return (
    <>
      {/* 1 ── Classic blue hero */}
      <PageHeading
        eyebrow={t("legal:hero.eyebrow")}
        title={t("legal:hero.title")}
        intro={t("legal:hero.intro")}
        actions={<>
          <DSButton to={TRIAL} variant="solid-light">{t("legal:cta.startTrial")}</DSButton>
          <DSButton to="/contact" variant="ghost">{t("legal:cta.talkToUs")}</DSButton>
        </>}
      />

      {/* 2 ── What changes — lighting-up blue Workflow with stacking cards */}
      <Workflow
        eyebrow={<Eyebrow dark>{t("legal:changes.eyebrow")}</Eyebrow>}
        title={<>{t("legal:changes.title.lead")} <span className="text-ds-ink">{t("legal:changes.title.accent")}</span></>}
        description={t("legal:changes.description")}
        note={t("legal:changes.note")}
        steps={changeSteps}
        cta={{
          title: t("legal:changes.cta.title"),
          body: t("legal:changes.cta.body"),
          action: <DSButton to={TRIAL} variant="solid-light">{t("legal:cta.startTrial")}</DSButton>,
        }}
      />

      {/* 3 ── Before 2027 — sector bento */}
      <MotionProvider>
        <section className="bg-bg-subtle font-ds">
          <Container className="py-16 md:py-20">
            <SectionHeading
              eyebrow={t("legal:sectors.eyebrow")}
              title={t("legal:sectors.title")}
              intro={t("legal:sectors.intro")}
            />
            <div className="mt-10 rounded-3xl border border-line bg-surface p-2">
              <div className="grid gap-2 sm:grid-cols-2">
                {SECTORS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Reveal key={s.name} className="flex">
                      <LocaleLink to={`/blog/${s.slug}`} className="group flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5 md:p-6 transition-colors hover:bg-white">
                        <div className="flex items-center justify-between">
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                            <Icon size={20} strokeWidth={2} />
                          </span>
                          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-ink-faint shadow-sm">{s.since}</span>
                        </div>
                        <h3 className="mt-4 font-display text-ds-18 font-semibold text-ink">{s.name}</h3>
                        <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ink-soft">{s.body}</p>
                        <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                          {t("legal:sectors.readMore")} <ArrowRight size={14} strokeWidth={2} />
                        </span>
                      </LocaleLink>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* + ── Big 75vh photo before the timeline */}
      <section className="bg-bg-subtle font-ds">
        <div className="px-4 py-3">
          <div className="relative h-[75vh] overflow-hidden rounded-[32px] bg-ds-hero">
            <img src={cmsImage(t, "legal:photo", PHOTO)} alt={t("legal:photo.alt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            <div aria-hidden className="absolute inset-0" style={{ background: SCRIM }} />
          </div>
        </div>
      </section>

      {/* 4 ── Animated gold timeline */}
      <GoldTimeline />

      {/* 5 ── Readiness checklist + CTA (DS) */}
      <section className="bg-bg font-ds">
        <Container className="grid gap-10 py-16 md:py-20 lg:grid-cols-2 lg:gap-12">
          <div>
            <SectionHeading eyebrow={t("legal:checklist.eyebrow")} title={t("legal:checklist.title")} />
            <ul className="mt-8 flex flex-col gap-3">
              {CHECKLIST.map((c) => (
                <li key={c} className="flex items-center gap-3 rounded-2xl border-4 border-white bg-bg-subtle px-4 py-3.5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ds-pos text-white"><Check size={13} strokeWidth={3} /></span>
                  <span className="text-ds-14 leading-snug text-ds-ink">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative flex flex-col justify-center overflow-hidden rounded-[32px] bg-ds-hero p-8 md:p-10">
            <div className="pointer-events-none absolute inset-0" style={GRID_W} />
            <div className="relative">
              <Eyebrow dark>{t("legal:compliant.eyebrow")}</Eyebrow>
              <h3 className="mt-4 font-display text-ds-26 font-semibold tracking-tight text-white lg:text-ds-30">{t("legal:compliant.title")}</h3>
              <p className="mt-3 text-ds-14 leading-relaxed text-white/70">
                {t("legal:compliant.body")}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <DSButton to={TRIAL} variant="solid-light">{t("legal:cta.startTrial")}</DSButton>
                <DSButton to="/contact" variant="ghost">{t("legal:cta.talkToUs")}</DSButton>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 7 ── Related reading — up to 4 articles */}
      {RELATED.length > 0 && (
        <section className="bg-bg-subtle font-ds">
          <Container className="py-16 md:py-20">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div>
                <SectionHeading eyebrow={t("legal:related.eyebrow")} title={t("legal:related.title")} />
              </div>
              <LocaleLink to="/resources" className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline">
                {t("legal:related.allArticles")} <ArrowRight size={14} strokeWidth={2} />
              </LocaleLink>
            </div>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {RELATED.map((p) => (
                <PostCard
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  tag={p.tag}
                  title={p.title}
                  read={p.read}
                  date={POST_DATE[p.slug]}
                  image={coverFor(p)}
                />
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* 8 ── Closing CTA */}
      <PromoBanner
        eyebrow={t("legal:closing.eyebrow")}
        title={t("legal:closing.title")}
        body={t("legal:closing.body")}
        action={<DSButton to={TRIAL} variant="solid-light">{t("legal:cta.startTrial")}</DSButton>}
      />
    </>
  );
}

